<template>
  <Transition name="fade" appear>
    <GridsTwelveGrid>
      <div class="col-span-6 col-start-1 row-start-1 mt-4 flex flex-col laptop:col-span-5 laptop:col-start-2">
        <img
          class="max-h-[100px] max-w-[100px] object-contain"
          width="100"
          height="100"
          :src="props.brand?.attributes?.logo?.data?.attributes?.url"
          :alt="props.brand?.attributes?.logo?.data?.attributes?.alternativeText || ''"
        />
        <h1 class="mt-1 laptop:mt-4">{{ props.product?.attributes?.name }}</h1>
        <div class="col-span-6 col-start-1 hidden laptop:col-span-5 laptop:col-start-2 laptop:mt-8 laptop:block">
          <div>
            {{ props.product?.attributes?.description }}
          </div>
          <ul class="mt-6 space-y-6" v-if="props.product?.attributes?.product_cuts?.data.length">
            <li
              v-for="cut in props.product?.attributes?.product_cuts?.data"
              :key="JSON.stringify(cut)"
              class="flex items-center space-x-2"
            >
              <div class="flex h-[40px] w-[40px] items-center justify-center">
                <img
                  class="h-[28px] w-[28px]"
                  width="28"
                  height="28"
                  v-if="cut.attributes?.icon?.data?.attributes?.url"
                  :src="cut.attributes?.icon?.data?.attributes?.url"
                  :alt="cut.attributes?.icon?.data?.attributes?.alternativeText || ''"
                />
                <div class="h-[28px] w-[28px]" v-else></div>
              </div>
              <span class="text-[15px] capitalize">{{ cut.attributes?.name }}</span>
            </li>
          </ul>
          <div class="mt-6 flex items-center capitalize" v-if="ingredients">
            <div class="flex h-[40px] w-[40px] items-center justify-center">
              <img src="/images/ingredients.png" width="28" height="28" />
            </div>
            <div class="ml-2">{{ $t("with") }} {{ ingredients }}</div>
          </div>
          <div class="mt-6 capitalize" v-if="ingredients"></div>
          <div
            class="mt-6 flex items-center"
            v-if="props.product?.attributes?.in_picture_size_text || props.product?.attributes?.in_picture_size?.data"
          >
            <div class="flex h-[40px] w-[40px] items-center justify-center">
              <img src="/icons/frame.svg" width="25" height="25" />
            </div>
            <div class="ml-2">
              {{ $t("in-this-photo") }}:
              <span v-if="props.product?.attributes?.in_picture_size_text">
                {{ props.product?.attributes?.in_picture_size_text || "" }}
              </span>
              <span v-else>
                {{
                  props.product?.attributes?.in_picture_size?.data?.attributes?.parent?.data?.attributes
                    ?.in_picture_title || ""
                }}
                {{ props.product?.attributes?.in_picture_size?.data?.attributes?.name || "" }} </span
              >.
              {{ $t("see-all") }}
              <NuxtLink class="link text-link-color-green" :to="localePath({ name: 'products-packaging' })">
                <span class="underlined relative leading-[1.2]">{{ $t("packaging-options") }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-6 col-start-1 laptop:col-span-4 laptop:col-start-8">
        <img
          :src="props.product?.attributes?.image?.data?.attributes?.url"
          :alt="props.product?.attributes?.image?.data?.attributes?.alternativeText || ''"
          class="h-auto w-full object-contain"
        />
      </div>
      <div class="col-span-6 col-start-1 laptop:col-span-5 laptop:col-start-2 laptop:hidden">
        <div>
          {{ props.product?.attributes?.description }}
        </div>
        <ul class="mt-6 space-y-6" v-if="props.product?.attributes?.product_cuts?.data.length">
          <li
            v-for="cut in props.product?.attributes?.product_cuts?.data"
            :key="JSON.stringify(cut)"
            class="flex items-center space-x-2"
          >
            <div class="flex h-[40px] w-[40px] items-center justify-center">
              <img
                class="h-[28px] w-[28px]"
                width="28"
                height="28"
                :src="cut.attributes?.icon?.data?.attributes?.url"
                :alt="cut.attributes?.icon?.data?.attributes?.alternativeText || ''"
              />
            </div>
            <span class="text-[15px] capitalize">{{ cut.attributes?.name }}</span>
          </li>
        </ul>
        <div class="mt-6 flex items-center capitalize" v-if="ingredients">
          <div class="flex h-[40px] w-[40px] items-center justify-center">
            <img src="/images/ingredients.png" width="28" height="28" />
          </div>
          <div class="ml-2">{{ $t("with") }} {{ ingredients }}</div>
        </div>
        <div class="mt-6 capitalize" v-if="ingredients"></div>
        <div class="mt-6 flex items-center">
          <div class="flex h-[40px] w-[40px] items-center justify-center">
            <img src="/icons/frame.svg" width="25" height="25" />
          </div>
          <div class="ml-2">
            {{ $t("in-this-photo") }}: {{ props.product?.attributes?.image?.data?.attributes?.caption }}.
            {{ $t("see-all") }}
            <NuxtLink class="link" :to="localePath({ name: 'products-packaging' })">
              <span class="underlined relative leading-[1.2]">{{ $t("packaging-options") }}</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </GridsTwelveGrid>
  </Transition>
</template>
<script setup lang="ts">
import { ProductBrandInfoFragment, ProductFragment } from "#gql";
const props = defineProps<{
  brand: ProductBrandInfoFragment | null | undefined;
  product?: ProductFragment | null | undefined;
}>();
const { t: $t } = useI18n();

const localePath = useLocalePath();

/**
 *
 * Given an array of ingredients of the form [{attributes: {name: "ingredient1"}}, {attributes: {name: "ingredient2"}}]
 * return a string of the form "ingredient1, ingredient2 & ingredient3"
 *
 **/

const ingredients = computed(() => {
  const ingredients = props.product?.attributes?.product_ingredients?.data?.map((item) => item.attributes?.name);
  if (ingredients?.length) {
    return ingredients.join(", ").replace(/,(?!.*,)/gim, " & ");
  }
});
</script>
<style lang="scss" scoped>
.link > .underlined {
  padding-bottom: 2px;
  &::after {
    content: "";
    width: 100%;
    height: 8px;
    position: absolute;
    background: theme("colors.links-highlight");
    left: 0;
    bottom: 2px;
    z-index: -1;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.22s ease-out;
  }
}

.link {
  &:hover {
    > .underlined::after {
      transform: scaleX(1);
      // background: theme("colors.ng800");
    }
  }

  &.router-link-active {
    > .underlined::after {
      transform: scaleX(1);
      // background: theme("colors.link-color-green");
    }
  }
}
</style>
